import React, { createContext, useContext, useEffect, useState } from "react";
import { useAppStore } from "@/hooks/useAppStore";
import { useAudioRecorder, useAudioRecorderStore } from "@/hooks/useAudioRecorder";

import type { RealTimeVADOptions } from "@ricky0123/vad-web";
import { getDefaultRealTimeVADOptions, MicVAD } from "@ricky0123/vad-web";
import { VADEagerness } from "@/types";

import { useWakeLock } from "@/hooks/useWakeLock";
import { addWarnFilter, containsFilter } from "@/lib/consoleFilter";

const VADContext = createContext<any>(null);

// VAD Eagerness Presets
// Inspired by OpenAI's realtime VAD eagerness concept
// Controls how aggressively the VAD sends audio for transcription
export interface VADEagernessConfig {
  minAudioDuration: number;  // Minimum audio before sending (ms)
  maxAudioDuration: number;  // Maximum audio before force-sending (ms)
  maxSilenceDuration: number; // Maximum silence before sending accumulated audio (ms)
}

export const VAD_EAGERNESS_PRESETS: Record<VADEagerness, VADEagernessConfig> = {
  // HIGH eagerness: Quick to send, good for rapid back-and-forth conversation
  high: {
    minAudioDuration: 500,   // 2 seconds
    maxAudioDuration: 8000,  // 10 seconds
    maxSilenceDuration: 800, // 1 second
  },
  // MEDIUM eagerness: Balanced approach for most conversations
  medium: {
    minAudioDuration: 5000,   // 5 seconds
    maxAudioDuration: 12000,  // 12 seconds
    maxSilenceDuration: 1200, // 1.5 seconds
  },
  // LOW eagerness: Patient, waits for complete thoughts and long exposition
  low: {
    minAudioDuration: 9000,   // 9 seconds
    maxAudioDuration: 15000,  // 15 seconds
    maxSilenceDuration: 2000, // 2 seconds
  },
};

export const VADProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const setVADListening = useAppStore((state) => state.setVADListening);
  const vadEagerness = useAppStore((state) => state.vadEagerness);

  const { recorder, isInitializing } = useAudioRecorder();
  const [loading, setLoading] = useState(true);
  const [listening, setListening] = useState(false);
  const [vad, setVAD] = useState<MicVAD | null>(null);
  const [errored, setErrored] = useState<false | string>(false);
  const setVADEnabled = useAppStore((state) => state.setVADEnabled);
  const isVADEnabled = useAppStore((state) => state.isVADEnabled);
  const isPIPActive = useAppStore((state) => state.isPIPActive);

  // Prevent device sleep when VAD is enabled
  useWakeLock(isVADEnabled);

  const [resumeVAD, setResumeVAD] = useState(false);
  const isAdmin = !!useAppStore(state => state.adminUser);

  // Get the current VAD eagerness configuration
  const vadConfig = VAD_EAGERNESS_PRESETS[vadEagerness];

  useEffect(() => {
    if (isInitializing || !recorder?.encodeAudio || isAdmin) {
      return;
    }

    let myvad: MicVAD | null;
    let canceled = false;
    let startedAt = 0;
    // Audio accumulation for minimum duration
    let audioBuffers: Float32Array[] = [];
    let totalAudioDuration = 0; // in milliseconds
    let lastSpeechEndTime = 0;
    let isSpeaking = false;

    // Helper function to send accumulated audio
    const sendAccumulatedAudio = async (reason: string) => {
      if (audioBuffers.length === 0) return;

      // console.log(`TTTT: Sending accumulated audio - Duration: ${totalAudioDuration}ms, Buffers: ${audioBuffers.length}, Reason: ${reason}`);

      try {
        // Concatenate all accumulated audio buffers
        const totalLength = audioBuffers.reduce((sum, buf) => sum + buf.length, 0);
        const concatenated = new Float32Array(totalLength);
        let offset = 0;
        for (const buffer of audioBuffers) {
          concatenated.set(buffer, offset);
          offset += buffer.length;
        }

        const opusBlob = await recorder.encodeAudio(concatenated);
        useAppStore.getState().sendAudio(opusBlob);
      } catch (error) {
        useAppStore.getState().showQosMessage("VAD encoding error", 2000);
      } finally {
        // Reset accumulation
        audioBuffers = [];
        totalAudioDuration = 0;
        lastSpeechEndTime = 0;
      }
    };

    // v5 uses frame size of 512
    // Assumption is 16000 Hz audio
    // 1 frame = 512/16000 * 1000 = 32ms
    // redemptionFrames of 8 = 8*32 = 256ms
    const vadOptions: RealTimeVADOptions = {
      ...getDefaultRealTimeVADOptions("v5"),
      model: "v5",
      frameSamples: 512,
      positiveSpeechThreshold: 0.5,
      negativeSpeechThreshold: 0.35,
      redemptionFrames: 8,
      preSpeechPadFrames: 3,
      minSpeechFrames: 12,
      submitUserSpeechOnPause: true,
      onFrameProcessed() {
        if (startedAt && (Date.now() - startedAt) > vadConfig.maxAudioDuration) {
          // Automatically end speech if it exceeds max duration
          myvad?.pause();
          // Restart VAD to listen for new speech
          myvad?.start();
          startedAt = 0;
        }

        // Check if we're in silence with accumulated audio
        if (!isSpeaking && audioBuffers.length > 0 && lastSpeechEndTime > 0) {
          const silenceDuration = Date.now() - lastSpeechEndTime;
          if (silenceDuration >= vadConfig.maxSilenceDuration) {
            lastSpeechEndTime = 0;
            sendAccumulatedAudio('MAX_SILENCE_EXCEEDED');
          }
        }
      },
      onSpeechEnd: async (audio: Float32Array) => {
        const speechDuration = Date.now() - startedAt;
        console.log(`TTTT: OnSpeechEnd ${speechDuration}ms`);
        setVADListening(false);
        startedAt = 0;
        isSpeaking = false;

        // Add this audio segment to the buffer
        audioBuffers.push(audio);
        const audioDurationMs = (audio.length / 16000) * 1000;
        totalAudioDuration += audioDurationMs;
        lastSpeechEndTime = Date.now();

        // Check if we should send based on minimum duration
        if (totalAudioDuration >= vadConfig.minAudioDuration) {
          await sendAccumulatedAudio('MIN_DURATION_MET');
        } else {
          // console.log(`TTTT: Accumulating audio - Total duration: ${totalAudioDuration}ms, Buffers: ${audioBuffers.length}`);
          // Silence will be checked in onFrameProcessed
        }
      },
      onSpeechStart: () => {
        setVADListening(true);
        startedAt = Date.now();
        isSpeaking = true;
      },
      onVADMisfire: () => {
        // console.log(`TTTT: OnVADMisfire ${Date.now() - startedAt}ms`);
        setVADListening(false);
        isSpeaking = false;
      },
    };
    const setup = async (): Promise<void> => {
      // Suppress ONNX Runtime warnings about unused initializers during model load.
      // These warnings are benign - ONNX Runtime optimizes the model by removing unused tensors.
      // This is a known issue with Silero VAD models and doesn't affect functionality.
      // Reference: search for CleanUnusedInitializersAndNodeArgs issues on the web
      addWarnFilter('onnx-initializer', containsFilter('Removing initializer'));

      try {
        const stream = useAudioRecorderStore.getState().mediaStream;
        if (stream) {
          vadOptions.stream = stream;
        }
        myvad = await MicVAD.new(vadOptions);
        if (canceled) {
          myvad.destroy();
          return;
        }
      } catch (e) {
        setLoading(false);
        if (e instanceof Error) {
          setErrored(e.message);
        } else {
          setErrored(String(e));
        }
        return;
      }
      setVAD(myvad);
      setLoading(false);
      if (isVADEnabled) {
        myvad.start();
        setListening(true);
      }
    };
    setup().catch((_e) => {
      console.log("Well that didn't work");
    });
    return function cleanUp() {
      myvad?.destroy();
      canceled = true;
      if (!loading && !errored) {
        setListening(false);
      }
    };
  }, [isInitializing, isVADEnabled, recorder, vadEagerness]);

  const pause = () => {
    if (!loading && !errored) {
      vad?.pause();
      setListening(false);
    }
  };
  const start = () => {
    if (!loading && !errored) {
      vad?.start();
      setListening(true);
    }
  };
  const toggle = () => {
    if (listening) {
      pause();
    } else {
      start();
    }
  };

  const toggleVAD = () => {
    const newState = !isVADEnabled;
    setVADEnabled(newState);
    // Also update presentation settings if in presentation mode
    const isPresentationMode = useAppStore.getState().isPresentationMode;
    if (isPresentationMode) {
      useAppStore.getState().setPresentationSettings({ vadEnabled: newState });
    }
  };

  useEffect(() => {
    if (isVADEnabled !== listening) {
      toggle();
    }
  }, [isVADEnabled, listening]);

  useEffect(() => {
    return; 
    /**
     * Currently disabling the effect where vad is disabled on page blur
     */
    if (isInitializing) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.hidden && !isPIPActive) {
        setVADEnabled(false);
        if (listening) {
          setResumeVAD(true);
        }
      }
      if (!document.hidden && resumeVAD) {
        setVADEnabled(true);
        setResumeVAD(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isPIPActive, resumeVAD, listening, isInitializing]);

  useEffect(() => {
    const store = useAppStore.getState();
    store.toggleVAD = () => {
      setVADEnabled(!useAppStore.getState().isVADEnabled);
    };
    return () => {
      store.toggleVAD = () => {};
    };
  }, []);

  return (
    <VADContext.Provider
      value={{
        listening,
        errored,
        loading,
        pause,
        start,
        toggle,
        toggleVAD
      }}
    >
      {children}
    </VADContext.Provider>
  );
};

export const useVAD = () => {
  const context = useContext(VADContext);
  if (!context) {
    throw new Error("useVAD must be used within a VADProvider");
  }
  return context;
};
